import Parse from "parse"
import { MachineModels } from "./machineModelsManager"
import { MachineTypes } from "./machineTypesManager"
import { KitchenArea } from "../kitchenAreas/kitchenAreasManager"
import { escapeRegExp } from "lodash"
import { withoutMachineName, sortMachinesByNames } from "../../utils/machinesutils"

const Machines = Parse.Object.extend("Machines")

export const getMachinesByKitchenArea = async (
  kitchenArea,
  filters,
  includes = ["machineModel", "kitchenArea"],
  toJson = true
) => {
  try {
    const query = await new Parse.Query(Machines)
      .equalTo("kitchenArea", kitchenArea)
      .include(includes)

    if (filters) {
      if (filters.name) {
        const regex = new RegExp(escapeRegExp(filters.name), "ig")
        query.matches("name", regex)
      }
    }
    const machines = (await query.find()) || []

    return toJson ? machines.map((machine) => machine.toJSON()) : machines
  } catch (e) {
    console.error(
      "parseManager.machine.machinesManager.getMachinesByKitchenArea error : ",
      e
    )
    return Promise.reject(e)
  }
}

export const getMachinesByKitchenAreaId = async (
  kitchenAreaId,
  filters,
  select = ["name", "objectId", "machineModel"],
  toJson = true
) => {
  try {
    const kitchenArea = new KitchenArea()
    kitchenArea.id = kitchenAreaId

    const query = new Parse.Query(Machines)
      .equalTo("kitchenArea", kitchenArea)
      .select(select)

    if (filters) {
      if (filters.name) {
        const regex = new RegExp(escapeRegExp(filters.name), "ig")
        query.matches("name", regex)
      }
    }

    const machines = (await query.find()) || []

    return toJson ? machines.map((machine) => machine.toJSON()) : machines
  } catch (e) {
    console.error("parseManager.machine.machinesManager.getMachinesByKitchenAreaId error : ",e)
    return Promise.reject(e)
  }
}

/**
 * Get machines split by transformation type for "Prépa chaude" kitchen area
 * Returns { cookingMachines: [], coolingMachines: [] } for "Prépa chaude"
 * Returns { allMachines: [] } for other kitchen areas
 * Each machine object contains: { objectId, name }
 */
export const getMachinesByKitchenAreaIdSplitByType = async (
  kitchenAreaId,
  filters,
  toJson = true
) => {
  try {
    // First, get the kitchen area to check its name
    const kitchenAreaQuery = new Parse.Query(KitchenArea)
      .equalTo("objectId", kitchenAreaId)
      .select(["name"])
    
    const kitchenAreaObj = await kitchenAreaQuery.first()
    const kitchenAreaName = kitchenAreaObj?.get("name")
    
    // Get all machines for this kitchen area
    const kitchenArea = new KitchenArea()
    kitchenArea.id = kitchenAreaId

    const machinesQuery = new Parse.Query(Machines)
      .equalTo("kitchenArea", kitchenArea)
      .select(["name", "objectId", "machineModel", "linkedMachine"])
      .include(["machineModel", "linkedMachine"])

    if (filters?.name) {
      const regex = new RegExp(escapeRegExp(filters.name), "ig")
      machinesQuery.matches("name", regex)
    }

    const machines = (await machinesQuery.find()) || []
    
    // If not "Prépa chaude", return all machines in a single array
    if (kitchenAreaName !== "Prépa chaude") {
      const allMachines = toJson ? machines.map(machine => machine.toJSON()) : machines
      return { allMachines: sortMachinesByNames(allMachines) }
    }
    
    // For "Prépa chaude", split by transformation type
    const cookingMachines = []
    const coolingMachines = []
    
    // Process each machine to determine its type
    for (const machine of machines) {
      const machineModel = machine.get("machineModel")
      
      if (machineModel) {
        // Query MachineTypes to get transformation types
        const machineTypeQuery = new Parse.Query(MachineTypes)
          .equalTo("machineModels", machineModel)
          .select(["transformationTypes"])
          .first()
        
        const machineType = await machineTypeQuery
        
        if (machineType) {
          const transformationTypes = machineType.get("transformationTypes") || []
          
          if (transformationTypes.includes("COOKING")) {
            cookingMachines.push(toJson ? machine.toJSON() : machine)
          } else if (transformationTypes.includes("COOLING")) {
            coolingMachines.push(toJson ? machine.toJSON() : machine)
          }
        }
      }
    }

    return {
      cookingMachines: sortMachinesByNames(cookingMachines),
      coolingMachines: sortMachinesByNames(coolingMachines)
    }

  } catch (e) {
    console.error("parseManager.machine.machinesManager.getMachinesByKitchenAreaIdSplitByType error : ", e)
    return Promise.reject(e)
  }
}

export const getWithoutMachine = async () => {
  const regex = new RegExp(escapeRegExp(withoutMachineName), "ig")
  const machine = await new Parse.Query(Machines)
    .matches("name", regex)
    .first()

    return machine?.toJSON()
}

export const countMachinesByKitchenArea = async (kitchenArea) => {
    try {
      const count = await new Parse.Query(Machines)
        .equalTo("kitchenArea", kitchenArea)
        .count()

    return count
    } catch (e) {
      console.error(
        "parseManager.machine.machinesManager.countMachinesByKitchenArea error : ",
        e
      )

      return Promise.reject(e)
    }
  }

export const getMachinesById = async (
  id,
  includes = ["machineModel", "kitchenArea"],
  toJson = true
) => {
  try {
    const machine = await new Parse.Query(Machines)
      .equalTo("objectId", id)
      .include(includes)
      .first()

    return toJson ? machine.toJSON() : machine
  } catch (e) {
    console.error(
      "parseManager.machine.machinesManager.getMachinesById error : ",
      e
    )
    return Promise.reject(e)
  }
}

export const createMachine = async (values, toJSON = true) => {
  try {
    // ------------ Machines ------------ //
    const machine = new Machines()
    const machineModel = new MachineModels()
    const kitchenArea = new KitchenArea()

    machine.set("name", values.name)

    machineModel.id = values.machineModelId
    machine.set("machineModel", machineModel)

    kitchenArea.id = values.kitchenAreaId
    machine.set("kitchenArea", kitchenArea)

    // Set linked machine if provided
    if (values.linkedMachineId) {
      const linkedMachine = new Machines()
      linkedMachine.id = values.linkedMachineId
      machine.set("linkedMachine", linkedMachine)
    }

    const newMachine = await machine.save()

    return toJSON ? newMachine.toJSON() : newMachine
  } catch (e) {
    console.error(
      "parseManager.machine.machinesManager.createMachine error : ",
      e
    )
    return Promise.reject(e)
  }
}

export const updateMachine = async (id, values, toJSON = true) => {
  try {
    // ------------ Machines ------------ //
    const machine = await getMachinesById(id, [], false)

    const machineModel = new MachineModels()
    const kitchenArea = new KitchenArea()

    machine.set("name", values.name)

    machineModel.id = values.machineModelId
    machine.set("machineModel", machineModel)

    kitchenArea.id = values.kitchenAreaId
    machine.set("kitchenArea", kitchenArea)

    // Set linked machine if provided, or unset it if empty
    if (values.linkedMachineId) {
      const linkedMachine = new Machines()
      linkedMachine.id = values.linkedMachineId
      machine.set("linkedMachine", linkedMachine)
    } else machine.unset("linkedMachine")

    const newMachine = await machine.save()

    return toJSON ? newMachine.toJSON() : newMachine
  } catch (e) {
    console.error(
      "parseManager.machine.machinesManager.createMachine error : ",
      e
    )
    return Promise.reject(e)
  }
}

export const deleteMachinesByIds = async (ids) => {
  try {
    await new Parse.Query(Machines)
      .containedIn("objectId", ids)
      .each(async (machine) => {
        await machine.destroy()
      })
  } catch (e) {
    console.error(
      "parseManager.machine.machinesManager.deleteMachinesByIds error : ",
      e
    )
    return Promise.reject(e)
  }
}
